import { Store } from '../redux/Store'
import axios, { AxiosRequestConfig, AxiosResponse } from 'axios' // eslint-disable-line no-unused-vars
import { toast } from 'react-toastify'
import memoizee from 'memoizee'
import moment from 'moment'
import qs from 'qs'
import { servicesUnavailable } from 'app/views/routes'

export function getModelChangeEvent(djangoApp, djangoModel) {
  return new Event(`hall:modelchange:${djangoApp}:${djangoModel}`)
}

// File extension for a downloaded report, from its response's content type
export const reportFileExtension = (contentType = '', fallback = 'pdf') => {
  if (contentType.includes('html')) return 'html'
  if (contentType.includes('pdf')) return 'pdf'
  if (contentType.includes('spreadsheetml')) return 'xlsx'
  return fallback
}

export class BaseHttpHandler {
  static _shared = null

  _httpClient = axios

  constructor(httpClient) {
    this._httpClient = httpClient
    if (!this._httpClient.interceptors.response.handlers.length)
      this._httpClient.interceptors.response.use(
        (response) => response,
        (error) => {
          this.clearMemos()
          if (error.message === 'Network Error') {
            if (window.navigator.onLine) {
              if (window.location.pathname !== servicesUnavailable.path) {
                const servicesUnavailablePageUrl = new URL(
                  servicesUnavailable.path,
                  window.location.origin
                )
                servicesUnavailablePageUrl.searchParams.set(
                  'next',
                  window.location.pathname
                )
                window.location = servicesUnavailablePageUrl.toString()
              }
            } else {
              // Reloading the page will cause the app to re-render and show the offline page
              window.location.reload()
            }
            return Promise.reject(error)
          } else if (error.response && error.response.status === 401) {
            localStorage.clear()
            window.location.href = '/'
          } else {
            if (error.response && error.response.data) {
              const { message } = error.response.data

              if (message) toast.error(message, { autoClose: 10000 })
            }
            return Promise.reject(error)
          }
        }
      )

    if (!this._httpClient)
      throw new Error(
        'Api client cannot be in instantiated without a httpClient.'
      )

    // if (process.env.NODE_ENV !== 'development')
    this._get = memoizee(this._rawGet, {
      normalizer: JSON.stringify,
      max: 20,
      maxAge: 30 * 60 * 1000,
    })
  }

  /**
   * Returns a shared api instance
   *
   * @returns {BaseHttpHandler} Api shared instance
   */
  static get shared() {
    if (this._shared === null) this._shared = new this(axios)
    return this._shared
  }

  get _defaultHeaders() {
    const { auth } = Store.getState()

    const headers = {}
    if (auth) headers['authorization'] = `Token ${auth.token}`

    return headers
  }

  clearMemos() {
    try {
      this._get.clear()
    } catch (error) {
      console.warn('Unable to clear memos')
    }
  }

  /**
   * Handle a http request
   *
   * @param {string} method Http request method
   * @param {AxiosRequestConfig} config Axios config object
   */
  _request(method = 'GET', config = {}) {
    if (!window.navigator.onLine) {
      return Promise.reject(new Error('Unable to make call when offline'))
    }
    if (method !== 'GET') this.clearMemos()
    return this._httpClient.request({
      timeout: 10000,
      ...config,
      method,
      baseURL: process.env.REACT_APP_BACKEND_URL,
      headers: {
        ...this._defaultHeaders,
        ...(config?.headers || {}),
      },
    })
  }

  /**
   * Handle an http GET request
   *
   * @param {string} url Request url
   * @param {AxiosRequestConfig} config Axios config object
   */
  _rawGet = (url, config = {}) => {
    return this._request('GET', {
      url,
      ...config,
    })
  }
  _get = this._rawGet

  /**
   * Handle an http POST request
   *
   * @param {string} url Request url
   * @param {object} data Request data
   * @param {AxiosRequestConfig} config Axios config object
   */
  _post(url, data = {}, config = {}) {
    return this._request('POST', {
      url,
      data,
      ...config,
    })
  }

  /**
   * Handle an http PUT request
   *
   * @param {string} url Request url
   * @param {object} data Request data
   * @param {AxiosRequestConfig} config Axios config object
   */
  _put(url, data = {}, config = {}) {
    return this._request('PUT', {
      url,
      data,
      ...config,
    })
  }

  /**
   * Handle an http DELETE request
   *
   * @param {string} url Request url
   * @param {AxiosRequestConfig} config Axios config object
   */
  _delete(url, config = {}) {
    return this._request('DELETE', {
      url,
      ...config,
    })
  }

  async _fileDownload(
    url,
    fileName = null,
    method = 'GET',
    config = {},
    useFileSystemHandler = false
  ) {
    const supportsFileSystemAccess =
      'showSaveFilePicker' in window &&
      (() => {
        try {
          return window.self === window.top
        } catch {
          return false
        }
      })()
    let fileSystemHandler
    if (supportsFileSystemAccess && useFileSystemHandler) {
      try {
        // Show the file save dialog.
        fileSystemHandler = await window.showSaveFilePicker({
          suggestedName: fileName,
        })
      } catch (err) {
        console.warn(err.name, err.message)
      }
    }
    return this._request(method, {
      url,
      timeout: 5 * 60 * 1000,
      responseType: 'blob',
      ...config,
    })
      .then(
        ({ data, headers }) => [data, headers],
        (error) => {
          if (error.response) {
            const fr = new FileReader()

            fr.addEventListener('load', (e) => {
              try {
                const data = JSON.parse(fr.result)
                toast.error(data.message)
              } catch (error) {
                toast.error(fr.result)
              }
            })

            fr.readAsText(error.response.data)
          }
          throw error
        }
      )
      .then(async ([fileData, headers]) => {
        // A function picks the name from the response itself, for endpoints
        // whose file type isn't known until the response arrives
        if (typeof fileName === 'function') fileName = fileName(fileData)
        if (!fileName) {
          try {
            const contentDisposition = headers['content-disposition']
            const [_, ...args] = contentDisposition // eslint-disable-line
              .split(';')
              .map((v) => v.trim())
            const parsedArgs = Object.fromEntries(args.map((a) => a.split('=')))
            fileName = parsedArgs['filename']
          } catch (error) {
            console.warn(
              'Unable to parse filename from Content-Distribution',
              error
            )
          }
        }

        // If the File System Access API is supported…
        if (fileSystemHandler) {
          try {
            // Write the blob to the file.
            const writable = await fileSystemHandler.createWritable()
            await writable.write(fileData)
            await writable.close()
            return
          } catch (err) {
            console.error(err.name, err.message)
          }
        }

        const a = document.createElement('a')

        const url = URL.createObjectURL(fileData)
        a.href = url
        a.download = fileName
        a.click()
        return url
      })
  }

  list = async (app, model, filters, hydrate = []) => {
    if (!app || !model) throw Error('Unable to find app or model')
    return this._get(`${app}/api/list/${model}`, {
      params: {
        format: 'json',
        hydrate,
        ...Object.fromEntries(
          Object.entries(filters || {})
            .filter(
              ([key, value]) =>
                value !== null && value !== undefined && value !== ''
            )
            .map(([key, value]) => {
              if (typeof value == 'boolean') {
                // Convert booleans to the python way
                value = value ? 'True' : 'False'
              }
              return [key, value]
            })
        ),
      },
      paramsSerializer: (params) =>
        qs.stringify(params, { arrayFormat: 'repeat' }),
    })
  }

  reportList = async (app, reportName, filters) => {
    if (!app || !reportName) throw Error('Unable to find app or report name')
    return this._get(`${app}/api/report/${reportName}`, {
      params: filters,
    })
  }

  create = (app, model, data) => {
    return this._post(`${app}/api/form/${model}`, data, {
      timeout: 2 * 60 * 1000,
    })
  }

  update = (app, model, data, id) => {
    return this._put(`${app}/api/form/${model}/${id}`, data, {
      timeout: 2 * 60 * 1000,
    })
  }

  delete = (app, model, id) => {
    return this._delete(`${app}/api/form/${model}/${id}`)
  }

  uploadExerciseVideo = (file) => {
    return this._post(
      'activities/api/form/exercise-video',
      { file, original_filename: file.name },
      { timeout: 5 * 60 * 1000 }
    )
  }

  // Nutrition: send a meal photo to be interpreted by the AI. Returns an
  // editable draft (`{ is_food, items, notes, image }`) - nothing is saved
  // until the caller logs the items it keeps through the regular form APIs.
  interpretMealPhoto = (photo, description = '') => {
    const body = new FormData()
    body.append('photo', photo, photo.name || 'refeicao.jpg')
    if (description) body.append('description', description)
    return this._post('nutrition/api/interpret-meal-photo', body, {
      timeout: 2 * 60 * 1000,
    })
  }

  matchExerciseVideo = (videoId, exerciseId) => {
    return this._post(`activities/api/exercise-video/${videoId}/match`, {
      exercise_id: exerciseId,
    })
  }

  form = (app, model, id = '') => {
    return this._get(`${app}/api/form/${model}${id ? `/${id}` : ''}`)
  }

  listPendingSurveys = () => {
    return this._rawGet('surveys/api/list/survey/pending')
  }

  getSurveyToFill = (surveyId) => {
    return this._rawGet(`surveys/api/self-fill/survey/${surveyId}`)
  }

  // Staff (collaborator+) filling a survey on a user's behalf, or reading an
  // existing response to resume/edit it in the fill modal.
  getSurveyToFillForUser = (surveyId) => {
    return this._rawGet(`surveys/api/fill/survey/${surveyId}`)
  }

  // Answer-by-answer saving: `answers` are the rows of a single question
  // (empty clears it); a response stays in progress until completed.
  saveSelfFillAnswer = (surveyId, question, answers) => {
    return this._put(`surveys/api/self-fill/survey/${surveyId}/answer`, {
      question,
      answers,
    })
  }

  completeSelfFill = (surveyId) => {
    return this._post(`surveys/api/self-fill/survey/${surveyId}/complete`)
  }

  startSurveyForUser = (surveyId, userId, question, answers) => {
    return this._post(`surveys/api/fill/survey/${surveyId}/answer`, {
      user: userId,
      question,
      answers,
    })
  }

  saveSurveyResponseAnswer = (responseId, question, answers) => {
    return this._put(`surveys/api/fill/survey-response/${responseId}/answer`, {
      question,
      answers,
    })
  }

  completeSurveyResponse = (responseId) => {
    return this._post(`surveys/api/fill/survey-response/${responseId}/complete`)
  }

  getSurveyResponseToEdit = (responseId) => {
    return this._rawGet(`surveys/api/fill/survey-response/${responseId}`)
  }

  updateSurveyResponseAnswers = (responseId, answers) => {
    return this._put(`surveys/api/fill/survey-response/${responseId}`, {
      answers,
    })
  }

  /**
   * Surveys to list on a user's profile ("Questionários" tab): every survey
   * the user has answered plus every one prompted in the app, each with the
   * user's `response_count` and `last_response_at`.
   */
  getUserSurveySummary = (userId) => {
    return this._rawGet(`surveys/api/user-survey-summary/${userId}`)
  }

  printSurveyResponse = (responseId, fileName) => {
    return this._fileDownload(
      this.getPrintPath('surveys', 'survey-response', responseId),
      fileName
    )
  }

  /**
   * Side-by-side history of a user's answers to one survey: the three latest
   * responses plus the first one, like the physical evaluation history.
   */
  printSurveyResponseHistory = (userId, surveyId, fileName) => {
    return this._fileDownload(
      this.getPrintPath('surveys', 'survey-response-history', userId),
      fileName,
      'GET',
      { params: { survey: surveyId } },
      true
    )
  }

  /**
   * An empty copy of a survey, to be filled on paper: nothing is stored.
   * `userId` is optional and only pre-fills the person's name.
   */
  printBlankSurvey = (surveyId, fileName, userId = null) => {
    return this._fileDownload(
      this.getPrintPath('surveys', 'survey', surveyId),
      fileName,
      'GET',
      { params: userId ? { user: userId } : {} },
      true
    )
  }

  chart = (app, model, params = {}) => {
    return this._get(`${app}/api/chart/${model}`, {
      params,
      timeout: 50 * 60 * 1000,
    }).then((data) => data?.data)
  }

  kpi = (app, model, viewMode, params = {}) => {
    return this._get(`${app}/api/kpi/${viewMode}/${model}`, { params }).then(
      (data) => data?.data
    )
  }

  financialMovementSummary = (params = {}) => {
    return this._get(`finances/api/financial-movement-summary`, {
      params: Object.fromEntries(
        Object.entries(params).filter(
          ([, value]) => value !== null && value !== undefined && value !== ''
        )
      ),
      paramsSerializer: (params) =>
        qs.stringify(params, { arrayFormat: 'repeat' }),
    }).then((data) => data?.data)
  }

  get = async (app, model, id, hydrate = []) => {
    const config = {
      params: {
        hydrate,
      },
      paramsSerializer: (params) =>
        qs.stringify(params, { arrayFormat: 'repeat' }),
    }
    if (['activities'].includes(app))
      return await this._get(`${app}/api/retrieve/${model}/${id}`, config).then(
        (data) => data?.data
      )
    return await this._get(
      `${app}/api/list/${model}?id=${id}&paginate_by=1`,
      config
    ).then((data) => data?.data?.data?.[0])
  }

  savePushToken = (token) => {
    return this._post(`users/api/push-notification-token`, { token })
  }

  qrCodeActivation = (user_id) => {
    return this._post(`users/api/qr-code-activation`, { user_id })
  }

  convenioCredencial = () => {
    return this._get(`convenios/api/credencial`)
  }

  validarConvenioUso = (qr_token, convenio_id, establishment) => {
    return this._post(`convenios/api/validar-uso`, {
      qr_token,
      convenio_id,
      establishment,
    })
  }

  convenioInsights = (params) => {
    return this._get(`convenios/api/insights`, { params })
  }

  emailActivation = (user_id, prefered_email = null) => {
    return this._post(
      `users/api/email-activation`,
      { user_id, prefered_email },
      { timeout: 60000 }
    )
  }

  activationLink = (user_id) => {
    return this._post(`users/api/activation-link`, { user_id })
  }

  resetUserPassword = (user_id) => {
    return this._post(`users/api/reset-password`, { user_id })
  }

  getMergeSuggestions = () => {
    return this._rawGet(`users/api/merge-suggestions`)
  }

  listDataSharingRequests = (direction) => {
    return this._rawGet(`data-sharing/api/requests`, { params: { direction } })
  }

  createDataSharingRequest = ({ origin_database, target_cpf_cnpj, reason }) => {
    return this._post(`data-sharing/api/requests`, {
      origin_database,
      target_cpf_cnpj,
      reason,
    })
  }

  approveDataSharingRequest = (share_id) => {
    return this._post(`data-sharing/api/requests/${share_id}/approve`)
  }

  rejectDataSharingRequest = (share_id, rejection_reason) => {
    return this._post(`data-sharing/api/requests/${share_id}/reject`, {
      rejection_reason,
    })
  }

  revokeDataSharingRequest = (share_id) => {
    return this._post(`data-sharing/api/requests/${share_id}/revoke`)
  }

  getDataSharingAuditLog = (share_id) => {
    return this._rawGet(`data-sharing/api/requests/${share_id}/audit-log`)
  }

  mergeUsers = (source_user_id, target_user_id) => {
    return this._post(`users/api/merge`, {
      source_user_id,
      target_user_id,
    })
  }

  authenticate = (username, password, totpCode) => {
    return this._post(`authentication/`, {
      username,
      password,
      code: totpCode,
    })
  }

  changePassword = (new_password, current_password = undefined) => {
    return this._post('authentication/change-user-password', {
      current_password,
      new_password,
    })
  }

  _parseUserData = (personalDataType, value) => {
    if (['phone', 'cpf', 'cnpj'].includes(personalDataType))
      return value.replace(/[^\d]/g, '')
    return value
  }

  getUserContacts = (personalDataType, value) => {
    return this._post('users/api/v2/user-contacts', {
      [personalDataType]: this._parseUserData(personalDataType, value),
    })
  }

  checkSelfSignupCpfAvailability = (cpf) => {
    return this._get('users/api/self-signup/cpf-availability', {
      params: { cpf: this._parseUserData('cpf', cpf) },
    })
  }

  sendActivationOTP = (personalDataType, personalData, contact) => {
    return this._get('users/api/user-account-activation-otp', {
      timeout: 60000,
      params: {
        [personalDataType]: this._parseUserData(personalDataType, personalData),
        contact,
      },
    })
  }

  verifyActivationOTP = (personalDataType, personalData, otp) => {
    return this._post('users/api/user-account-activation-otp', {
      [personalDataType]: personalData.replace(/[^\d]/g, ''),
      otp,
    })
  }

  getUserInfo = (token) => {
    return this._get(`users/api/user-info`, {
      headers: {
        authorization: `Token ${token}`,
      },
    })
  }

  // Deliberately `_rawGet`, not the memoized `_get`: memoization here caches
  // the *promise itself* (not its resolved value — `_get` isn't configured
  // with memoizee's `promise: true`), so a failed attempt would keep
  // returning the same rejected promise for up to 30 minutes. Feature flags
  // must be re-fetched on every login attempt, including right after a
  // previous one failed.
  getFeatureFlags = () => {
    return this._rawGet(`feature-flags/api/feature-flags/`)
  }

  listInternalCampaigns = () => {
    return this._rawGet(`internal-campaigns/api/campaigns/`)
  }

  dismissInternalCampaign = (id) => {
    return this._post(`internal-campaigns/api/campaigns/${id}/dismiss/`)
  }

  clickInternalCampaignCta = (id) => {
    return this._post(`internal-campaigns/api/campaigns/${id}/cta-click/`)
  }

  // TODO: Deprecate this in favor of better cookie handling through domain
  persistSession = (token, next, newTab = false) => {
    const path = `${
      process.env.REACT_APP_BACKEND_URL
    }/authentication/login?${new URLSearchParams({
      token,
      next: next.replace(/&/g, '%26'),
    })}`

    if (newTab) window.open(path)
    else window.location.href = path
  }

  getMediaUrl = (uri) => {
    if (uri?.startsWith('http')) return uri
    return `${process.env.REACT_APP_BACKEND_URL}/media/${uri}`
  }

  getSignedMediaUrl = (url) => {
    return this._get(`utilities/api/sign-media-url?media_path=${url}`)
  }

  getStorageUrl = (pathname, width, height) => {
    return `${process.env.REACT_APP_BACKEND_URL}/utilities/api/storage/${width}/${height}/${pathname}`
  }

  getPrintPath = (app, model, id) => {
    return `/${app}/api/print/${model}/${id}`
  }

  getPermission = (key) => {
    return this._rawGet(`users/api/user-data-permission/${key}`)
  }

  setPermission = (key, value) => {
    return this._post(`users/api/user-data-permission/${key}`, { value })
  }

  getPhysicalDeviceToken = () => {
    return this._rawGet(`physical-devices/api/physical-device-token/`)
  }

  getPhysicalSpaceReservationInviteToken = (id) => {
    return this._rawGet(
      `physical-spaces/api/qr/physical-space-reservation-invite/${id}`
    )
  }

  getFillContractUrl = (id) => {
    return `${process.env.REACT_APP_BACKEND_URL}/contracts/api/fill/${id}`
  }

  createTOTP = () => {
    return this._post('authentication/totp/create')
  }

  verifyTOTP = (code) => {
    return this._post('authentication/totp/verify', { code })
  }

  checkTOTPRequirement = (username) => {
    return this._post('authentication/totp/check-user-totp-configuration', {
      username,
    })
  }

  payPaymentOption = (paymentOptionId, paymentMethodId) => {
    return this._post(
      `finances/api/payment-option/bill/${paymentOptionId}`,
      {
        payment_method_id: paymentMethodId,
      },
      { timeout: 30000 }
    )
  }

  financialMovementOperation = (
    operation,
    movementsPerDocument,
    paymentMethodId
  ) => {
    return this._post(
      `finances/api/financial-movement/${operation}`,
      {
        movements: movementsPerDocument,
        payment_method_id: paymentMethodId,
      },
      { timeout: 30000 }
    )
  }

  generateHealthInsuranceComparisonReport = (
    cycle,
    referenceFile,
    providerIds
  ) => {
    return this._post(
      `health-insurance/api/health-insurance-comparison-report`,
      {
        cycle,
        health_insurance_reference_file: referenceFile,
        health_insurance_provider_ids: providerIds,
      },
      { timeout: 30000 }
    )
  }

  generateLifeInsuranceComparisonReport = (referenceFile) => {
    return this._post(
      `users/api/life-insurance-comparison-report`,
      {
        life_insurance_reference_file: referenceFile,
      },
      { timeout: 30000 }
    )
  }

  bookActivityClass = (activityClassId, classDate) => {
    return this._post(
      `activities/api/activity-class/${activityClassId}/next-classes`,
      {
        class_date: moment(classDate).format('YYYY-MM-DD'),
      }
    )
  }

  unbookActivityClass = (activityClassId, classDate) => {
    return this._delete(
      `activities/api/activity-class/${activityClassId}/next-classes?class_date=${moment(
        classDate
      ).format('YYYY-MM-DD')}`
    )
  }

  getCustomReportFields = (app, model) => {
    return this._get(`${app}/api/custom-report/${model}`)
  }

  getCustomReportFieldForm = (app, model, field, operator) => {
    return this._get(`${app}/api/custom-report/${model}/${field}/${operator}`)
  }

  // `format`: 'pdf' (the default) or 'xlsx'. Some 'pdf' reports are rendered
  // as HTML by the backend (`force_html`, e.g. financial-movement), so the
  // extension follows the response's content type, not the requested format.
  submitCustomReportForm = (app, model, formData, format = 'pdf') => {
    const isSpreadsheet = format === 'xlsx'
    const baseName = `${model}-report-${moment().format('DD-MM-YYYY_HH-mm')}`
    return this._fileDownload(
      `/${app}/custom-report/${model}${isSpreadsheet ? '/xlsx' : ''}`,
      (fileData) =>
        `${baseName}.${reportFileExtension(
          fileData?.type,
          isSpreadsheet ? 'xlsx' : 'pdf'
        )}`,
      'POST',
      { data: formData }
    )
  }

  downloadReport = (app, reportName, params) => {
    return this._fileDownload(
      `/${app}/api/report/${reportName}?${new URLSearchParams(params)}`,
      `${reportName}-report-${moment().format('DD-MM-YYYY_HH-mm')}`,
      'GET',
      { timeout: 90000 }
    )
  }

  previewNotification = (data) => {
    return this._post(`notifications/api/form/notification/preview`, data)
  }

  cancelContractPreview = (id) => {
    return this._get(
      `/contracts/api/contract-cancel?${new URLSearchParams({ id })}`
    )
  }

  cancelContract = (id, reason, reasonCategory = undefined) => {
    return this._post(`/contracts/api/contract-cancel`, {
      id,
      reason,
      reason_category: reasonCategory,
    })
  }

  cancellationReport = (params = {}) => {
    return this._get(`/contracts/api/cancellation-report`, {
      params: Object.fromEntries(
        Object.entries(params).filter(
          ([, value]) => value !== null && value !== undefined && value !== ''
        )
      ),
    })
  }

  contractTaskTimeline = (contractId) => {
    return this._get(`/management/api/contract-task-timeline/${contractId}`)
  }

  renewalFollowUp = (data) => {
    return this._post(`/management/api/renewal-follow-up`, data)
  }

  contractCrm = (filter = undefined, months = 3) => {
    return this._get(
      `/contracts/api/contract-crm?${new URLSearchParams({
        filter,
        months,
      })}`
    )
  }

  contractCrmExport = (filter = undefined, months = 3) => {
    return this._fileDownload(
      'contracts/api/contract-crm/export',
      `crm-de-contratos_${months}-meses.csv`,
      'GET',
      { params: { filter, months } }
    )
  }

  reorderEmbeddedRecords = (app, model, records) => {
    return this._post(`/${app}/api/order/${model}`, { records })
  }

  duplicateWorkout = (id) => {
    return this._post(`/activities/api/duplicate-workout/${id}`)
  }

  finishWorkoutLog = (id, durationSeconds) => {
    return this._post(`/activities/api/workout-log/${id}/finish`, {
      duration_seconds: durationSeconds,
    })
  }

  skipWorkoutExercise = (workoutLogId, exerciseId) => {
    return this._post(
      `/activities/api/workout-log/${workoutLogId}/skip-exercise`,
      {
        exercise_id: exerciseId,
      }
    )
  }

  // Logs a single set from the treino's page, outside a player session. The
  // backend files it under an "inferred_from_sets" workout log, reused while
  // the aluno keeps logging within 2h.
  logStandaloneSet = (workoutId, exerciseId, repetitions, weight) => {
    return this._post(`/activities/api/workouts/${workoutId}/log-set`, {
      exercise_id: exerciseId,
      repetitions,
      weight,
    })
  }

  // The aluno's last load on this exercise (`own`) and on up to 3 similar ones.
  // Not memoized: it must reflect sets logged moments ago.
  getExerciseSimilarWeights = (exerciseId, alunoId) => {
    return this._rawGet(
      `/activities/api/exercises/${exerciseId}/similar-weights`,
      { params: { aluno_id: alunoId } }
    )
  }

  frequentlySkippedExercises = (alunoId, limit, workoutId) => {
    return this._get('/activities/api/frequently-skipped-exercises', {
      params: { aluno_id: alunoId, limit, workout_id: workoutId },
    })
  }

  /**
   * Preview of the evaluation/survey data available for `userId`, shown in
   * the AI-workout-generation modal so a collaborator can decide which
   * checkboxes to tick before anything is sent to the AI.
   */
  getAiWorkoutGenerationContext = (userId) => {
    return this._rawGet('/activities/api/ai-workout-generation-context', {
      params: { user_id: userId },
    })
  }

  /**
   * Queues an asynchronous AI generation of one workout per selected week
   * day (0 = Monday .. 6 = Sunday) for `userId`. Only the evaluations /
   * survey responses listed in `evaluationIds` / `surveyResponseIds` are
   * shared with the AI. Resolves with the queued
   * job — poll `getAiWorkoutGenerationJob` until its `status` is `success`
   * or `error`. Generated workouts are created with status
   * `pending_approval` and must be approved (or rejected) by a
   * collaborator+ before they become available to the aluno.
   */
  generateAiWorkout = ({
    userId,
    weekDays,
    customPrompt = '',
    evaluationIds = [],
    surveyResponseIds = [],
  }) => {
    return this._post('/activities/api/generate-ai-workout', {
      user_id: userId,
      week_days: weekDays,
      custom_prompt: customPrompt,
      evaluation_ids: evaluationIds,
      survey_response_ids: surveyResponseIds,
    })
  }

  getAiWorkoutGenerationJob = (jobId) => {
    return this._rawGet(`/activities/api/ai-workout-generation-job/${jobId}`)
  }

  /**
   * Company-wide instructions sent with every AI workout generation prompt.
   * Readable by anyone who can generate; only administrators can update it.
   */
  updateAiWorkoutGenerationPreset = (preset) => {
    return this._put('/activities/api/ai-workout-generation-preset', {
      preset,
    })
  }

  approveAiWorkout = (workoutId) => {
    return this._post(`/activities/api/ai-workout/${workoutId}/approve`)
  }

  rejectAiWorkout = (workoutId) => {
    return this._post(`/activities/api/ai-workout/${workoutId}/reject`)
  }

  /**
   *
   * @param {Number} printerId ID of the printer to use
   * @param {Array<String>} documents List of documents to include in the receipt
   * @returns {Promise<AxiosResponse>}
   */
  printReceipt = (printerId, documents) => {
    return this._post(`/finances/api/print/financial-document-receipt`, {
      printer_id: printerId,
      documents,
    })
  }

  /**
   *
   * @param {string} djangoApp Django app name
   * @param {string} djangoModel Django model name
   * @param {number} objectId Django object id (defaults to null and open a obj creation if not provided)
   * @returns
   */
  getAdminUrl = (djangoApp, djangoModel, objectId = null) => {
    const sanitizeDjangoStr = (val) => val.replace(/-/g, '').toLowerCase()
    let url = [
      process.env.REACT_APP_BACKEND_URL,
      'admin',
      sanitizeDjangoStr(djangoApp),
      sanitizeDjangoStr(djangoModel),
    ].join('/')
    if (objectId) {
      url += `/${objectId}/change/`
    } else {
      url += `/add/`
    }
    return url
  }

  getBoletoPrintURL = (documentNumber, return_format = null) => {
    const url = new URL(
      `${process.env.REACT_APP_BACKEND_URL}/finances/api/print/financial-document`
    )
    url.searchParams.set('number', documentNumber)
    if (return_format) url.searchParams.set('return_format', return_format)
    return url.toString()
  }

  analyticsSummary = (params = {}) => {
    // Not memoized so the dashboard reload button always fetches fresh numbers
    return this._rawGet(`analytics/api/summary`, { params }).then(
      (response) => response?.data
    )
  }

  aiUsageSummary = (params = {}) => {
    // Not memoized so the dashboard reload button always fetches fresh numbers
    return this._rawGet(`analytics/api/ai-usage-summary`, { params }).then(
      (response) => response?.data
    )
  }

  benefitPointsDashboard = (params = {}) => {
    // Not memoized so the dashboard reload button always fetches fresh numbers.
    // Aggregates a whole period of logs (up to 12 months), which can take longer
    // than the default 10s on a large tenant
    return this._rawGet(`benefit-points/api/dashboard`, {
      params,
      timeout: 60000,
    }).then((response) => response?.data)
  }

  exportBenefitPointsDashboard = (params = {}) => {
    return this._fileDownload(
      `benefit-points/api/dashboard-export`,
      `pontos-de-beneficio_${params.start_date}_${params.end_date}.csv`,
      'GET',
      { params, timeout: 90000 }
    )
  }

  // Class roll call ("chamada") — see amsm-backend
  // hall/activities/attendance.py. Not memoized: a roll call changes as soon
  // as it's saved. (The attendance reports go through `list`, as
  // `ModelListPage` models.)
  getActivityClassRollCall = (classId, classDate) =>
    this._rawGet(`activities/api/activity-class/${classId}/roll-call`, {
      params: classDate ? { class_date: classDate } : {},
    }).then((response) => response?.data)

  saveActivityClassRollCall = (
    classId,
    classDate,
    presentUserIds,
    adHocEntries = []
  ) =>
    this._post(`activities/api/activity-class/${classId}/roll-call`, {
      class_date: classDate,
      present_user_ids: presentUserIds,
      ad_hoc_entries: adHocEntries,
    }).then((response) => response?.data)

  // Teacher area ("Área do professor") indicators — see amsm-backend
  // hall/activities/teacher_area.py. Not memoized: they change with every
  // roll call. (The teacher's classes go through `list`, as a
  // `ModelListPage` model.)
  teacherAreaInsights = (params = {}) =>
    this._rawGet('activities/api/teacher-area/insights', { params }).then(
      (response) => response?.data
    )

  // MCP ("Conectar assistente de IA") — see amsm-backend hall/mcp_server/api_views.py.
  // None memoized: connections and activity change under the user's feet.
  mcpSetup = () =>
    this._rawGet('mcp/api/setup').then((response) => response?.data)

  mcpConnections = () =>
    this._rawGet('mcp/api/connections').then((response) => response?.data)

  mcpDisconnect = (clientId) =>
    this._delete(`mcp/api/connections/${encodeURIComponent(clientId)}`)

  mcpActivity = () =>
    this._rawGet('mcp/api/activity').then((response) => response?.data)

  // Not memoized so the dashboard reload button always fetches fresh numbers
  mcpUsageSummary = (params = {}) =>
    this._rawGet('mcp/api/usage-summary', { params }).then(
      (response) => response?.data
    )

  mcpAuthorizationRequest = (flowParams) =>
    this._rawGet('mcp/api/authorization', { params: flowParams }).then(
      (response) => response?.data
    )

  mcpAuthorize = (flowParams, decision) =>
    this._post('mcp/api/authorization', { ...flowParams, decision }).then(
      (response) => response?.data
    )

  createFinancialDocumentNFSe = (documentId) => {
    return this._post(`fiscal/api/financial-document-nfse/${documentId}`)
  }
}

const HttpHandler = BaseHttpHandler.shared
window._httpHandler = HttpHandler // For debugging purposes
export default HttpHandler
